import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { ChevronUp, ChevronDown, Pencil, Trash2, Plus } from "lucide-react";
import { useDietaryTags, useCreateDietaryTag, useUpdateDietaryTag, useDeleteDietaryTag, type DietaryTag } from "@/hooks/useDietaryTags";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

interface DietaryTagManagementSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DietaryTagManagementSheet({ open, onOpenChange }: DietaryTagManagementSheetProps) {
  const { data: tags } = useDietaryTags(true);
  const createTag = useCreateDietaryTag();
  const updateTag = useUpdateDietaryTag();
  const deleteTag = useDeleteDietaryTag();

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [formKey, setFormKey] = useState("");
  const [formLabel, setFormLabel] = useState("");
  const [isAdding, setIsAdding] = useState(false);

  const startAdd = () => {
    setEditingKey(null);
    setFormKey("");
    setFormLabel("");
    setIsAdding(true);
  };

  const startEdit = (tag: DietaryTag) => {
    setIsAdding(false);
    setEditingKey(tag.key);
    setFormKey(tag.key);
    setFormLabel(tag.label);
  };

  const cancelForm = () => {
    setIsAdding(false);
    setEditingKey(null);
    setFormKey("");
    setFormLabel("");
  };

  const handleSave = () => {
    if (!formLabel.trim()) return;
    if (isAdding) {
      const key = formKey.trim() || formLabel.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/(^_|_$)/g, "");
      const maxSort = tags?.reduce((max, t) => Math.max(max, t.sort_order), 0) ?? 0;
      createTag.mutate(
        { key, label: formLabel.trim(), sort_order: maxSort + 1 },
        { onSuccess: cancelForm }
      );
    } else if (editingKey) {
      updateTag.mutate(
        { key: editingKey, label: formLabel.trim() },
        { onSuccess: cancelForm }
      );
    }
  };

  const moveTag = (tag: DietaryTag, direction: "up" | "down") => {
    if (!tags) return;
    const sorted = [...tags].sort((a, b) => a.sort_order - b.sort_order);
    const idx = sorted.findIndex((t) => t.key === tag.key);
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const other = sorted[swapIdx];
    updateTag.mutate({ key: tag.key, sort_order: other.sort_order });
    updateTag.mutate({ key: other.key, sort_order: tag.sort_order });
  };

  const sorted = tags ? [...tags].sort((a, b) => a.sort_order - b.sort_order) : [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Manage Dietary Tags</SheetTitle>
          <SheetDescription>
            Add, edit, or reorder dietary tags. {sorted.length} tags total.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {(isAdding || editingKey) && (
            <div className="space-y-3 rounded-lg border border-primary/20 bg-muted/30 p-4">
              <p className="text-sm font-medium">{isAdding ? "New Tag" : "Edit Tag"}</p>
              {isAdding && (
                <div className="space-y-2">
                  <Label className="text-xs">Key (auto-generated if blank)</Label>
                  <Input
                    value={formKey}
                    onChange={(e) => setFormKey(e.target.value)}
                    placeholder="e.g. soy_free"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-xs">Label</Label>
                <Input
                  value={formLabel}
                  onChange={(e) => setFormLabel(e.target.value)}
                  placeholder="e.g. Soy-Free"
                  autoFocus
                  onKeyDown={(e) => e.key === "Enter" && handleSave()}
                />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSave} disabled={!formLabel.trim() || createTag.isPending || updateTag.isPending}>
                  {isAdding ? "Add" : "Save"}
                </Button>
                <Button size="sm" variant="ghost" onClick={cancelForm}>Cancel</Button>
              </div>
            </div>
          )}

          {!isAdding && !editingKey && (
            <Button variant="outline" size="sm" className="w-full" onClick={startAdd}>
              <Plus className="h-4 w-4 mr-2" /> Add Tag
            </Button>
          )}

          <Separator />

          <div className="space-y-1">
            {sorted.map((tag, idx) => {
              const isEditing = editingKey === tag.key;
              return (
                <div
                  key={tag.key}
                  className={`flex items-center gap-2 rounded-md px-3 py-2 group transition-colors ${isEditing ? "bg-accent" : "hover:bg-muted/50"}`}
                >
                  <div className="flex flex-col -my-1">
                    <button
                      onClick={() => moveTag(tag, "up")}
                      disabled={idx === 0}
                      className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-20"
                    >
                      <ChevronUp className="h-3 w-3" />
                    </button>
                    <button
                      onClick={() => moveTag(tag, "down")}
                      disabled={idx === sorted.length - 1}
                      className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-20"
                    >
                      <ChevronDown className="h-3 w-3" />
                    </button>
                  </div>

                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium truncate">{tag.label}</span>
                    <p className="text-xs text-muted-foreground">{tag.key}</p>
                  </div>

                  <Switch
                    checked={tag.is_active}
                    onCheckedChange={(checked) => updateTag.mutate({ key: tag.key, is_active: checked })}
                    className="shrink-0"
                  />
                  <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 opacity-0 group-hover:opacity-100" onClick={() => startEdit(tag)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 opacity-0 group-hover:opacity-100">
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete "{tag.label}"?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This tag will be removed. Existing menu items with this tag will keep it in their data but it won't appear in the UI.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => deleteTag.mutate(tag.key)}>Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              );
            })}

            {sorted.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">
                No dietary tags yet. Add one to start tagging menu items.
              </p>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
